/**
 * @module StatusService
 * @param  {object} root    window/node global
 * @param  {function} factory status service factory
 * @return {object}         Status service
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define('statusService', ['repositoryService', 'localizationServiceProxy'], function (repositoryService, localizationServiceProxy) {
      return factory(root, repositoryService, localizationServiceProxy);
    });
  } else {
    root.StatusService = factory(root, repositoryService, localizationServiceProxy);
  }
})(this, function (root, repositoryService, localizationServiceProxy) {

  /**
   * @constant
   * @type {Array}
   */
  var INTERNAL_USERS = ['KNOWN_LOCATION', 'THD_STORE_LOCATION', 'DEFAULT_INTERNAL'];

  /**
   * @constant
   * @type {Array}
   */
  var EXTERNAL_USERS = ['LOC_HISTORY_BY_IP', 'LOC_HISTORY_BY_SUBNET', 'GEO_LOCATION', 'DEFAULT'];

  /**
   * @constant
   * @type {Array}
   */
  var GPS_WORKFLOW = ['LOCALIZED_BY_GPS_HIGH', 'LOCALIZED_BY_GPS_MEDIUM', 'LOCALIZED_BY_GPS_LOW'];

  var userService = {};

  /**
   * One year in milli seconds
   * @type {long}
   */
  var oneYear = 365 * 24 * 60 * 60 * 1000;
  var session = 0;
  var page = -1;

  /**
   * @member
   * @description A workflow is mapped to
   *   - Category Eg: Self/Auto/Default localized.<br>
   *   - Accuracy over other workflows.<br>
   *   - Alias name used to store workflow in digital data.
   * @type {WorkflowMapper}
   */
  var workflowMapper = {
    // client workflow
    LOCALIZED_BY_GPS_HIGH: { category: '1', accuracy: 'HIGH', alias: 'gps/html5/wi-fi|high' },
    LOCALIZED_BY_STORE: { category: '0', accuracy: 'HIGH', alias: 'IP-DB:Self Localized' },
    LOCALIZED_BY_FUZZY_STORE: { category: '0', accuracy: 'MEDIUM', alias: 'IP-DB:Self Localized' },
    LOCALIZED_BY_DEFAULT: { category: '2', accuracy: 'LOW', alias: 'Default' },
    LOCALIZED_BY_GPS_MEDIUM: { category: '1', accuracy: 'MEDIUM', alias: 'gps/html5/wi-fi|med' },
    LOCALIZED_BY_GPS_LOW: { category: '1', accuracy: 'LOW', alias: 'gps/html5/wi-fi|low' },
    // service workflow
    THD_STORE_LOCATION: { category: '1', accuracy: 'HIGH', alias: 'IP-DB:Store' },
    KNOWN_LOCATION: { category: '1', accuracy: 'HIGH', alias: 'IP-DB:Corporate' },
    LOC_HISTORY_BY_IP: { category: '1', accuracy: 'MEDIUM', alias: 'IP-DB:History' },
    LOC_HISTORY_BY_SUBNET: { category: '1', accuracy: 'LOW', alias: 'IP-DB:Similar History' },
    GEO_LOCATION: { category: '1', accuracy: 'MEDIUM', alias: 'IP-DB:Vendor' },
    DEFAULT_INTERNAL: { category: '2', accuracy: 'LOW', alias: 'IP-DB:Default Internal' },
    DEFAULT: { category: '2', accuracy: 'LOW', alias: 'Default' }
  }


  userService.workflowMapper = workflowMapper;

  /**
   * @member UserService.status
   * @type {Object}
   */
  userService.status = {

    /**
     * @function isInternal
     * @description Returns true if the user localized is Internal else false.
     * Checks if workflow is known_loc/store_loc/customer_reps(default-internal) or
     * THD_INTERNAL cookie for 1.
     * @param  {String}  workflow - workflow, an Optional
     * @return {Boolean}  True if Internal else false
     */
    isInternal: function (workflow) {
      var workflow = userService.status.getWorkflow();
      var thdInternal = repositoryService.getValue('THD_INTERNAL');
      return (workflow && INTERNAL_USERS.indexOf(workflow) !== -1) || thdInternal === '1';
    },

    /**
     * @function isInternalAsync
     * @description This is async version of isInternal()
     * Invokes localization service to determine if user is internal or not.
     * Resolves a promise wrapping true if user is internal else rejects promise wrapping false.
     * @example
     * if(Localizer.status.isUnknownUser()) {
     *   Localizer.status.isInternalAsync().then(function(){
     *     // business logic
     *   })
     * }
     *
     * @return {Promise} Promise wrapping a boolean value.
     */
    isInternalAsync: function () {
      return new Promise(function (resolve, reject) {
        localizationServiceProxy.getMetadata().then(function (response) {
          if (INTERNAL_USERS.indexOf(response.metadata.workflow) !== -1) {
            repositoryService.setValue('THD_INTERNAL', '1');
            resolve(true);
          } else {
            repositoryService.setValue('THD_INTERNAL', '0');
            reject(false);
          }
        }, function (error) {
          reject(false);
        });
      });
    },

    /**
     * @function isExternal
     * @description Returns true if the user localized is External else false.
     * Checks if workflow is loc_history_ip/loc_history_subnet/geo-location/default or
     * THD_INTERNAL cookie for 0.
     * @param  {String}  [workflow] - localization workflow
     * @return {Boolean}  True if External else false
     */
    isExternal: function (workflow) {
      workflow = workflow || userService.status.getWorkflow();
      var thdInternal = repositoryService.getValue('THD_INTERNAL');
      return (workflow && EXTERNAL_USERS.indexOf(workflow) !== -1) || thdInternal === '0';
    },

    /**
     * @function isSelfLocalized
     * @description Returns true if the user self_localized/localized_by_store. Checks THD_FORCE_LOC for 0
     * @return {Boolean} True if user self localized else false.
     */
    isSelfLocalized: function () {
      var thdForceLoc = repositoryService.getValue('THD_FORCE_LOC');
      return thdForceLoc === '0';
    },

    /**
     * @function isAutoLocalized
     * @description Returns true if the user auto_localized. Checks THD_FORCE_LOC for 1
     * @return {Boolean} True if user auto localized else false.
     */
    isAutoLocalized: function () {
      var thdForceLoc = repositoryService.getValue('THD_FORCE_LOC');
      return thdForceLoc === '1';
    },

    /**
     * @function isDefaultLocalized
     * @description Returns true if the user default_localized. Checks THD_FORCE_LOC for 2
     * @return {Boolean} True if user default localized else false.
     */
    isDefaultLocalized: function () {
      var thdForceLoc = repositoryService.getValue('THD_FORCE_LOC');
      return thdForceLoc === '2' || thdForceLoc === undefined || thdForceLoc === '';
    },

    /**
     * @function isUnknownUser
     * @description Returns true if the user neither internal nor external. Checks THD_INTERNAL is undefined or doesn't exist.
     * @return {Boolean} True if user default localized else false.
     */
    isUnknownUser: function () {
      var value = repositoryService.getValue('THD_INTERNAL');
      return value === undefined;
    },

    /**
     * @function isLocalized
     * @description Returns true if user is localized.
     * Following values are verified before deciding on whether user is localized or not.
     * 1. THD_LOC_STORE cookie should be set
     * 2. THD_FORCE_LOC cookie should be set either 0 or 1
     * 3. THD_INTERNAL cookie should be set to either 0 or 1
     *
     * @return {Boolean} Returns true if user is localized else false.
     */
    isLocalized: function () {
      var workflow = userService.status.getWorkflow();
      var localStore = repositoryService.getValue('THD_LOCSTORE');
      var localStoreExists = localStore ? true : false;
      return workflow && localStoreExists &&
        (this.isSelfLocalized() || this.isAutoLocalized() || this.isDefaultLocalized());
    },

    /**
     * @function isNotLocalized
     * @description Checks if the user is localized or not.
     * @see isLocalized
     * @return {Boolean} Returns true if user is not localized.
     */
    isNotLocalized: function () {
      return !this.isLocalized();
    },

    /**
     * @function isUserInStore
     * @description Returns true if user is accessing hd.com from store location else false
     * @todo Definition of InStore user might extend post geo-fencing implementation.
     * @return {Boolean} Returns true if user is in one of the HD stores
     */
    isUserInStore: function () {
      // supporting THD_INSTORE as is it set by Autlocalizer in header. Should be removed in future.
      // checking two source is not good !
      return this.getWorkflow() === 'THD_STORE_LOCATION';
    },

    /**
     * @function isUserInStoreAsync
     * @description This is async version of isUserInStore()
     * Resolves a promise wrapping true if user is accessing hd.com from store location else
     * a rejects a promise wrapping false
     * @example
     * if(Localizer.status.isSelfLocalized()) {
     *   Localizer.status.isUserInStore().then(function(){
     *     // business logic
     *   })
     * }
     * @return {Promise} Returns Promise wrapping true if user is in one of the HD stores
     */
    isUserInStoreAsync: function () {
      return new Promise(function (resolve, reject) {
        localizationServiceProxy.getMetadata().then(function (response) {
          if ('THD_STORE_LOCATION' === response.metadata.workflow) {
            resolve(true);
          } else {
            reject(false);
          }
        }, function (error) {
          reject(false);
        });
      });
    },

    /**
     * @function getAccuracy
     * @description Returns the accuracy of the localization mode.<br>
     * 1. If user is self localized then localization mode is treated as HIGH accuracy.<br>
     * 2. If user is auto localized then localization mode is treated as HIGH, MEDIUM, LOW accuracy.<br>
     * 3. If user is default localized then localization mode is treated as LOW accuracy
     * @see workflowMapper
     * @return {string} Returns HIGH|MEDIUM|LOW accuracy of the current localization mode.
     */
    getAccuracy: function () {
      var workflow = userService.status.getWorkflow();
      return workflow && workflowMapper[workflow].accuracy;
    },

    /**
     * @function getWorkflow
     * @description Returns the workflow by which localization was resolved.
     * @return {string} Returns workflow
     */
    getWorkflow: function () {
      return repositoryService.getValue('WORKFLOW');
    },

    /**
     * @function getGpsStatus
     * @description Reads GPS_STATUS from storage.
     * @return {string} Returns GPS status Eg: gps access granted, gps access denied, gps timeout
     */
    getGpsStatus: function () {
      return repositoryService.get('GPS_STATUS');
    },

    /**
     * @function getStoreNumberIfUserIsInsideStore
     * @description Reads GPS_STATUS from storage.
     * @return {string} Returns GPS status Eg: gps access granted, gps access denied, gps timeout
     */
    getStoreNumberIfUserIsInsideStore: function () {
      return repositoryService.get('IN_STORE_USER_NUMBER');
    },

    /**
     * @function getSessionToPreventUserInStoreCall
     * @description Reads cookie status created to keep a check on geo loc calls, for finding if user is in store
     * @return {string} Returns cookie status
     */
    getSessionToPreventUserInStoreCall: function () {
      return repositoryService.get('IN_STORE_API_SESSION');
    },

    /**
     * @function getLocalizationMethod
     * @description Returns an alias to the workflow. This is api intended for analytics
     * @return {string} Returns an alias to workflow.
     */
    getLocalizationMethod: function () {
      var workflow = this.getWorkflow();
      return workflow ? workflowMapper[workflow].alias : '';
    },

    /**   
    * Check if the request is from mobile app. User agent has the value = THDCustomer
    */
    isRequestFromConsumerApp: function () {
      return navigator.userAgent.indexOf("THDConsumer") > -1;
    },
    /**   
    * Check if store hours already expired
    */
    isStoreHoursExpired: function () {
      var hours = repositoryService.getValue('THD_STORE_HOURS_EXPIRY') * 1000;
      return new Date().getTime() > hours;
    }
  }

  /**
   * @member UserService.operations
   * @description User Service with methods to perform write operation.
   * @type {Object}
   */
  userService.operations = {

    /**
     * @function setGpsStatus
     * @description Persist gps status in storage
     * @param {string} gpsStatus GPS status Eg: gps access granted, gps access denied, gps timeout
     */
    setGpsStatus: function (gpsStatus) {
      repositoryService.put('GPS_STATUS', gpsStatus);
    },

    /**
     * @function setStoreNumberIfUserIsInsideStore
     * @description This is to set the store number of which store a user is in if they access HD.com from inside a store.  This will be used for Analytics tracking and may be different than the users localized store.
     * @param {string} storeNumber: will either be the store number or "not in store"
     */
    setStoreNumberIfUserIsInsideStore: function (storeNumber) {
      repositoryService.put('IN_STORE_USER_NUMBER', storeNumber);
    },

    /**
     * @function setSessionToPreventUserInStoreCall
     * @description Sets a cookie to keep a check on geo loc calls to determine if user is in store (allows api calls only after cookie expiry)
     */
    setSessionToPreventUserInStoreCall: function () {
      repositoryService.storageStrategy['cookie'].put('IN_STORE_API_SESSION', 'TRUE', 5 * 60 * 1000);
    },

    /**
     * @function markAsInStoreUser
     * @description Marks the user as in-store user.
     * @param  {string} workflow localization workflow
     */
    markAsInStoreUser: function (workflow) {
      if (workflow === 'THD_STORE_LOCATION') {
        // In the current system, THD_INSTORE cookie is set by AutoLocalizer service in header.
        repositoryService.put('THD_INSTORE', 'TRUE');
      }
    },

    /**
     * @function markAsCanadianUser
     * @description If the user accesses HD.com from canada marks those users as canadian users
     * by setting CA_USER cookie
     */
    markAsCanadianUser: function () {
      repositoryService.put('THD_CA_USER', 1);
      repositoryService.put('THD_AUTOLOCINTERCEPT', 1);
    }
  }

  return userService;

});



//////////////////
// WEBPACK FOOTER
// ./src/statusService.js
// module id = 4
// module chunks = 0